/**
 * Debug Logging Utility
 *
 * 전역 디버그 모드에 따라 콘솔 출력을 제어합니다.
 *
 * 사용법:
 *   import { debugLog, debugWarn, debugError } from './utils/debug.js';
 *   debugLog('[Component]', 'Message', data);
 *   debugWarn('[Warning]', error);
 *   debugError('[Error]', error);
 *
 * 디버그 모드 설정:
 *   localStorage.setItem('DEBUG_MODE', 'true');  // 활성화
 *   localStorage.setItem('DEBUG_MODE', 'false'); // 비활성화
 */

/**
 * 디버그 모드 확인
 * 우선순위:
 * 1. localStorage의 DEBUG_MODE 설정
 * 2. URL 파라미터 ?debug=true
 * 3. 기본값: 항상 활성화 (true)
 */
function isDebugMode() {
  // 1. localStorage 확인 (명시적으로 false 설정 시에만 비활성화)
  const localStorageDebug = localStorage.getItem('DEBUG_MODE');
  if (localStorageDebug === 'false') {
    return false;
  }

  // 2. URL 파라미터 확인
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.has('debug')) {
    return urlParams.get('debug') === 'true';
  }

  // 3. 기본값: 모든 환경에서 활성화
  return true;
}

/**
 * console.log 래퍼
 * 디버그 모드일 때만 출력
 */
export function debugLog(...args) {
  if (isDebugMode()) {
    console.log(...args);
  }
}

/**
 * console.warn 래퍼
 * 디버그 모드일 때만 출력
 */
export function debugWarn(...args) {
  if (isDebugMode()) {
    console.warn(...args);
  }
}

/**
 * console.error 래퍼
 * 디버그 모드일 때만 출력
 */
export function debugError(...args) {
  if (isDebugMode()) {
    console.error(...args);
  }
}

/**
 * console.info 래퍼
 * 디버그 모드일 때만 출력
 */
export function debugInfo(...args) {
  if (isDebugMode()) {
    console.info(...args);
  }
}

/**
 * console.debug 래퍼
 * 디버그 모드일 때만 출력
 */
export function debugDebug(...args) {
  if (isDebugMode()) {
    console.debug(...args);
  }
}

/**
 * console.table 래퍼
 * 디버그 모드일 때만 출력
 */
export function debugTable(...args) {
  if (isDebugMode()) {
    console.table(...args);
  }
}

/**
 * 디버그 모드 상태 확인
 */
export function getDebugMode() {
  return isDebugMode();
}

/**
 * 디버그 모드 설정
 */
export function setDebugMode(enabled) {
  localStorage.setItem('DEBUG_MODE', enabled ? 'true' : 'false');
  console.log(`[Debug] Debug mode ${enabled ? 'enabled' : 'disabled'}`);
}

/**
 * 항상 출력되는 로그 (디버그 모드와 무관)
 * 중요한 에러나 운영 로그용
 */
export function forceLog(...args) {
  console.log(...args);
}

export function forceWarn(...args) {
  console.warn(...args);
}

export function forceError(...args) {
  console.error(...args);
}
